import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface CommunicationRow {
  readonly portalCommunicationId: string;
  readonly communicationCode: string;
  readonly subject: string;
  readonly communicationType: string;
  readonly direction: string;
  readonly status: string;
}

const COMMUNICATION_TYPES = ['ANNOUNCEMENT', 'NOTIFICATION', 'MESSAGE', 'ALERT', 'REMINDER', 'NEWSLETTER', 'SURVEY', 'BROADCAST'];
const PAGE = 25;

/**
 * M42 §27 CommunicationCenter (§13). Two-way stakeholder communication: send
 * outbound messages and progress inbound ones through Read / Acknowledged.
 * Viewing needs M42_COMMUNICATION_VIEW, sending needs M42_COMMUNICATION_SEND.
 */
export function CommunicationCenter(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<CommunicationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [portalUserId, setPortalUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [communicationType, setCommunicationType] = useState('MESSAGE');
  const [bodyText, setBodyText] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: CommunicationRow[]; total: number }>(`/v1/portal/communications?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, page, reload]);

  const send = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!subject.trim()) { setActionError(t('m42.comms.subjectRequired')); return; }
    try {
      await apiRequest('/v1/portal/communications', {
        method: 'POST',
        body: { portalUserId: portalUserId.trim() || null, subject: subject.trim(), communicationType, body: bodyText.trim() || subject.trim() },
        token,
      });
      setNotice(t('m42.comms.sent'));
      setSubject('');
      setBodyText('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.comms.notAuthSend'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const progress = async (id: string, verb: 'read' | 'acknowledge'): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/portal/communications/${id}/${verb}`, { method: 'POST', body: {}, token });
      setNotice(verb === 'read' ? t('m42.comms.markedRead') : t('m42.comms.acknowledged'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.comms.notAuthUpdate'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="communication-center">
      <h1>{t('m42.comms.title')}</h1>

      <section aria-label="communication-send">
        <h2>{t('m42.comms.sendHeading')}</h2>
        <form aria-label="communication-form" onSubmit={(e) => void send(e)}>
          <RecordPicker<{ portalUserId: string; displayName?: string; email?: string }>
            endpoint="/v1/portal/users?limit=200"
            idField="portalUserId"
            label={t('m42.common.portalUser')}
            testId="field-portal-user"
            value={portalUserId}
            optionLabel={(u) => u.displayName ?? u.email ?? u.portalUserId}
            onPick={setPortalUserId}
            emptyText="No portal user on file"
          />
          <label>{t('m42.common.subject')}<input aria-label="field-comm-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
          <label>{t('m42.common.type')}
            <select aria-label="field-comm-type" value={communicationType} onChange={(e) => setCommunicationType(e.target.value)}>
              {COMMUNICATION_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m42.comms.bodyLabel')}<textarea aria-label="field-comm-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-communication">{t('m42.comms.sendBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="communication-action-error">{actionError}</p>}
        {notice && <p aria-label="communication-notice">{notice}</p>}
      </section>

      {error && <p role="alert" aria-label="communication-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="communication-total">{t('m42.comms.total', { count: total })}</p>
          <DataTable
            ariaLabel="communication-table"
            rows={rows}
            rowKey={(c) => c.portalCommunicationId}
            emptyMessage={t('m42.comms.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (c) => `communication-code-${c.portalCommunicationId}`, render: (c) => c.communicationCode },
              { key: 'subject', header: t('m42.common.subject'), render: (c) => c.subject },
              { key: 'type', header: t('m42.common.type'), render: (c) => c.communicationType },
              { key: 'direction', header: t('m42.comms.directionCol'), render: (c) => c.direction },
              { key: 'status', header: t('m42.common.status'), cellAriaLabel: (c) => `communication-status-${c.portalCommunicationId}`, render: (c) => c.status },
              { key: 'actions', header: t('m42.common.actions'), render: (c) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`read-${c.portalCommunicationId}`} onClick={() => void progress(c.portalCommunicationId, 'read')}>{t('m42.comms.markReadBtn')}</Button>
                  <Button variant="secondary" type="button" aria-label={`ack-${c.portalCommunicationId}`} onClick={() => void progress(c.portalCommunicationId, 'acknowledge')}>{t('m42.comms.ackBtn')}</Button>
                </>
              ) },
            ]}
          />

          <nav aria-label="communication-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
